import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

// Coach onboarding tooltip rollout, in ordered phases — Phase 1 (Profile /
// Stripe / Calendar), Phase 2 (Program / Ad), Phase 3 (Clients /
// Testimonials), Phase 4 (Messaging / Booking / Payments), Phase 5
// (Resource library). Only one phase is ever shown: the earliest phase that
// still has an incomplete step. A later phase doesn't appear until every
// step in the phase(s) before it is done — e.g. Phase 5 doesn't show until
// Phase 4's steps are all done too. Already-complete steps within the
// active phase are
// skipped. Dismissing (Skip, or finishing the last step) permanently
// dismisses that specific phase for the user — it shows once and never
// again, even if the phase's step(s) are still incomplete. Dismissing one
// phase does not affect another — so a newly-unlocked phase still shows
// once on its own. This runs alongside the separate first-time-only
// OnboardingSlideshow modal, which this component does not touch.
//
// Phase 4's "Payments" step has no independent signal — there's no stored
// payout flag, and checking real transaction state would mean a network
// call per client. It reuses the Booking step's `hasBookings` signal as a
// cheap proxy instead (not fully precise, but sufficient for a nudge).
const STORAGE_PREFIX = 'viveka_coach_onboarding_tour_dismissed_';

const TOOLTIP_WIDTH = 280;
const VIEWPORT_MARGIN = 12;

const CoachOnboardingTour = ({
  currentUser,
  stripeVerified,
  profileAnchorRef,
  settingsAnchorRef,
  programAnchorRef,
  adsAnchorRef,
  clientsAnchorRef,
  hasClients,
  hasTestimonials,
  messagesAnchorRef,
  sessionsAnchorRef,
  hasMessaged,
  hasBookings,
  resourcesAnchorRef,
  hasResources,
  phase3DataLoaded,
  phase4DataLoaded,
  phase5DataLoaded,
  active,
}) => {
  const userId = currentUser?.id?.uuid;

  const isProfileComplete = !!currentUser?.profileImage;
  const isAvailabilitySet = !!currentUser?.attributes?.profile?.privateData?.isAvailabilitySet;
  const isProgramCreated = !!currentUser?.attributes?.profile?.privateData?.isProgramCreated;
  const isListingCreated = !!currentUser?.attributes?.profile?.privateData?.isCreatedAListing;

  const activePhase = useMemo(() => {
    if (!currentUser) return null;

    const phase1Steps = [
      {
        key: 'profile',
        done: isProfileComplete,
        ref: profileAnchorRef,
        title: 'Add a profile photo',
        body: 'Coaches with a photo get more client interest. Add yours from your profile menu, top right.',
      },
      {
        key: 'stripe',
        done: stripeVerified,
        ref: settingsAnchorRef,
        title: 'Connect Stripe to get paid',
        body: 'Set up your payout account under Settings so you can start receiving payments from clients.',
      },
      {
        key: 'calendar',
        done: isAvailabilitySet,
        ref: settingsAnchorRef,
        title: 'Review your availability',
        body: "We've set a default availability for you. Review and adjust it under Settings so it matches when you're actually free to meet.",
      },
    ];
    if (phase1Steps.some(step => !step.done)) {
      return { id: 'phase1', steps: phase1Steps.filter(step => !step.done) };
    }

    const phase2Steps = [
      {
        key: 'program',
        done: isProgramCreated,
        ref: programAnchorRef,
        title: 'Create your first program',
        body: 'Package your coaching into a program so clients know what to book.',
      },
      {
        key: 'ad',
        done: isListingCreated,
        ref: adsAnchorRef,
        title: 'Publish an ad',
        body: 'Create a listing so clients can find and book you in the marketplace.',
      },
    ];
    if (phase2Steps.some(step => !step.done)) {
      return { id: 'phase2', steps: phase2Steps.filter(step => !step.done) };
    }

    // Phase 1/2 are derived straight from currentUser, already loaded by
    // the time this component mounts. Phase 3+ depend on separate async
    // fetches (clients/testimonials/activities/messages/resources) — until
    // those land, their steps default to "incomplete", which would flash a
    // tooltip for an already-completed step and then hide it once the real
    // data arrives. Show nothing rather than guess while waiting.
    if (!phase3DataLoaded) return null;

    const phase3Steps = [
      {
        key: 'clients',
        done: !!hasClients,
        ref: clientsAnchorRef,
        title: 'Add your first client',
        body: 'Invite a client so you can start scheduling sessions together.',
      },
      {
        key: 'testimonials',
        done: !!hasTestimonials,
        ref: settingsAnchorRef,
        title: 'Add a testimonial',
        body: 'Showcase social proof — request or add a testimonial under Settings.',
      },
    ];
    if (phase3Steps.some(step => !step.done)) {
      return { id: 'phase3', steps: phase3Steps.filter(step => !step.done) };
    }

    if (!phase4DataLoaded) return null;

    const phase4Steps = [
      {
        key: 'messaging',
        done: !!hasMessaged,
        ref: messagesAnchorRef,
        title: 'Send your first message',
        body: 'Reach out to a client — staying in touch keeps sessions on track.',
      },
      {
        key: 'booking',
        done: !!hasBookings,
        ref: sessionsAnchorRef,
        title: 'Get your first booking',
        body: 'Once your profile, program, and ad are live, clients can book a session with you.',
      },
      {
        key: 'payments',
        done: !!hasBookings,
        ref: settingsAnchorRef,
        title: "You're set up to get paid",
        body: 'Complete a booked session and your payout will land in the Stripe account you connected.',
      },
    ];
    if (phase4Steps.some(step => !step.done)) {
      return { id: 'phase4', steps: phase4Steps.filter(step => !step.done) };
    }

    if (!phase5DataLoaded) return null;

    const phase5Steps = [
      {
        key: 'resources',
        done: !!hasResources,
        ref: resourcesAnchorRef,
        title: 'Start your resource library',
        body: 'Upload a doc or video clients can access — a handy way to share guidance between sessions.',
      },
    ];
    if (phase5Steps.some(step => !step.done)) {
      return { id: 'phase5', steps: phase5Steps.filter(step => !step.done) };
    }

    return null;
  }, [
    currentUser,
    isProfileComplete,
    stripeVerified,
    isAvailabilitySet,
    isProgramCreated,
    isListingCreated,
    hasClients,
    hasTestimonials,
    phase3DataLoaded,
    hasMessaged,
    hasBookings,
    phase4DataLoaded,
    hasResources,
    phase5DataLoaded,
    profileAnchorRef,
    settingsAnchorRef,
    programAnchorRef,
    adsAnchorRef,
    clientsAnchorRef,
    messagesAnchorRef,
    sessionsAnchorRef,
    resourcesAnchorRef,
  ]);

  const steps = activePhase?.steps || [];
  const storageKey = userId && activePhase ? `${STORAGE_PREFIX}${userId}_${activePhase.id}` : null;

  const [stepIndex, setStepIndex] = useState(0);
  const [dismissed, setDismissed] = useState(true);
  const [rect, setRect] = useState(null);

  // Re-check on every mount (fresh page load/session) and whenever the
  // active phase changes — if THIS phase was already dismissed before, it
  // stays hidden for good; otherwise it's eligible to show.
  useEffect(() => {
    if (!storageKey) return;
    setDismissed(localStorage.getItem(storageKey) === 'true');
    setStepIndex(0);
  }, [storageKey]);

  const finish = useCallback(() => {
    if (storageKey) localStorage.setItem(storageKey, 'true');
    setDismissed(true);
  }, [storageKey]);

  const currentStep = !dismissed && active && steps.length > 0 ? steps[stepIndex] : null;

  useEffect(() => {
    if (!currentStep) {
      setRect(null);
      return undefined;
    }
    const updateRect = () => {
      const el = currentStep.ref?.current;
      setRect(el ? el.getBoundingClientRect() : null);
    };
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [currentStep]);

  if (!currentStep || !rect) return null;

  const isLast = stepIndex === steps.length - 1;
  const handleNext = () => (isLast ? finish() : setStepIndex(i => i + 1));

  const maxTop = window.innerHeight - VIEWPORT_MARGIN;
  const top = Math.min(rect.bottom + 12, maxTop - 140);
  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(rect.left, window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN)
  );

  // Portal straight to <body> so these fixed-position elements are always
  // positioned against the viewport — a transform/filter on any ancestor
  // elsewhere in the tree (page wrappers, hover effects, etc.) would
  // otherwise turn `position: fixed` into `position: absolute` relative to
  // that ancestor and throw the tooltip off its target.
  return ReactDOM.createPortal(
    <>
      <style>{`
        @keyframes coachOnboardingOverlayIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes coachOnboardingTooltipIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div
        aria-hidden="true"
        onClick={finish}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.65)',
          zIndex: 9997,
          animation: 'coachOnboardingOverlayIn 0.2s ease-out',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          borderRadius: '10px',
          boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.55), 0 0 16px 2px rgba(139, 92, 246, 0.35)',
          pointerEvents: 'none',
          zIndex: 9998,
        }}
      />
      <div
        role="dialog"
        aria-modal="false"
        aria-label={currentStep.title}
        style={{
          position: 'fixed',
          top,
          left,
          width: `${TOOLTIP_WIDTH}px`,
          zIndex: 9999,
          background: '#FFFFFF',
          color: '#0F172A',
          borderRadius: '12px',
          padding: '16px 18px',
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.35)',
          animation: 'coachOnboardingTooltipIn 0.2s ease-out',
        }}
      >
        <div
          style={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#7C3AED',
            marginBottom: '6px',
          }}
        >
          Step {stepIndex + 1} of {steps.length}
        </div>
        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700 }}>{currentStep.title}</h3>
        <p style={{ margin: '8px 0 14px', fontSize: '13px', lineHeight: 1.5, color: '#475569' }}>
          {currentStep.body}
        </p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            type="button"
            onClick={finish}
            style={{
              background: 'none',
              border: 'none',
              color: '#64748B',
              fontSize: '12px',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            Skip
          </button>
          <button
            type="button"
            onClick={handleNext}
            style={{
              background: 'linear-gradient(135deg, #7C3AED 0%, #8B5CF6 100%)',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {isLast ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default CoachOnboardingTour;
